// src/pages/components in pages/DashboardTutorEdit.jsx
import React, { useEffect, useMemo, useState } from "react";
import { gql, useMutation } from "@apollo/client";
import "../../styles/DashboardTutor.css";

// ===================
// CATEGORY MUTATIONS
// ===================
const CREATE_FACULTY_CATEGORY_MUTATION = gql`
  mutation CreateFacultyCategory($input: CreateFacultyCategoryInput!) {
    createFacultyCategory(input: $input) {
      id
      facultyId
      name
      slug
      position
    }
  }
`;

const UPDATE_FACULTY_CATEGORY_MUTATION = gql`
  mutation UpdateFacultyCategory($input: UpdateFacultyCategoryInput!) {
    updateFacultyCategory(input: $input) {
      id
      facultyId
      name
      slug
      position
    }
  }
`;

// ===================
// SUBJECT MUTATIONS
// ===================
const CREATE_SUBJECT_IN_CATEGORY_MUTATION = gql`
  mutation CreateSubjectInCategory($input: CreateSubjectInCategoryInput!) {
    createSubjectInCategory(input: $input) {
      position
      subject {
        id
        title
        slug
        description
        imageUrl
        isPublic
      }
      facultyCategory {
        id
      }
    }
  }
`;

const UPDATE_SUBJECT_MUTATION = gql`
  mutation UpdateSubject($input: UpdateSubjectInput!) {
    updateSubject(input: $input) {
      id
      title
      slug
      description
      imageUrl
      isPublic
      position
    }
  }
`;

// ===================
// Small inline SVG for search icon (blue)
// ===================
const SearchIcon2 = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path
      d="M16.2 16.2 21 21"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const DashboardTutorEdit = (props) => {
  const kind2 = props.kind2 || "category";
  if (!props.open2) return null;

  return kind2 === "subject" ? (
    <SubjectModal2 {...props} />
  ) : (
    <CategoryModal2 {...props} />
  );
};

// ===================
// CATEGORY MODAL
// ===================
const CategoryModal2 = ({
  open2,
  mode2,
  facultyId2,
  category2,
  onClose2,
  onSaved2,
}) => {
  const isEdit2 = mode2 === "edit";

  const initialName2 = useMemo(() => {
    if (isEdit2) return category2?.name || "";
    return "";
  }, [isEdit2, category2]);

  const [name2, setName2] = useState(initialName2);
  const [touched2, setTouched2] = useState(false);
  const [error2, setError2] = useState("");

  const [createCategory2, { loading: creating2 }] = useMutation(
    CREATE_FACULTY_CATEGORY_MUTATION
  );
  const [updateCategory2, { loading: updating2 }] = useMutation(
    UPDATE_FACULTY_CATEGORY_MUTATION
  );

  useEffect(() => {
    if (!open2) return;
    setName2(initialName2);
    setTouched2(false);
    setError2("");
  }, [open2, initialName2]);

  const saving2 = creating2 || updating2;

  const validate2 = () => {
    const trimmed = (name2 || "").trim();
    if (!trimmed) return "Bitte Fachkategoriename eingeben.";
    return "";
  };

  const onSave2 = async () => {
    setTouched2(true);
    const err = validate2();
    if (err) {
      setError2(err);
      return;
    }

    const trimmedName = name2.trim();

    try {
      if (isEdit2) {
        await updateCategory2({
          variables: { input: { id: category2.id, name: trimmedName } },
        });
      } else {
        await createCategory2({
          variables: { input: { facultyId: facultyId2, name: trimmedName } },
        });
      }

      if (onSaved2) await onSaved2();
    } catch (e) {
      setError2("Speichern fehlgeschlagen. Bitte erneut versuchen.");
    }
  };

  return (
    <>
      <div className="modal-backdrop2 modal-backdrop-edit2" onClick={onClose2} />

      <div className="category-modal2" role="dialog" aria-modal="true">
        <div className="category-modal-header2">
          <div className="category-modal-title2">
            {isEdit2 ? "Fachkategorie bearbeiten" : "Fachkategorie erstellen"}
          </div>
          <button
            className="category-modal-close2"
            type="button"
            aria-label="Schließen"
            onClick={onClose2}
          >
            ×
          </button>
        </div>

        <div className="category-modal-content2">
          <div className="category-modal-label2">Fachkategoriename*</div>

          <div className="category-modal-inputwrap2">
            <input
              className="category-modal-input2"
              type="text"
              value={name2}
              onChange={(e) => {
                setName2(e.target.value);
                if (touched2) setError2(validate2());
              }}
              onBlur={() => {
                setTouched2(true);
                setError2(validate2());
              }}
              placeholder={isEdit2 ? "" : "Fachkategoriename eingeben"}
              disabled={saving2}
            />
            <div className="category-modal-underline2" />
          </div>

          {!!error2 && <div className="category-modal-error2">{error2}</div>}
        </div>

        <div className="category-modal-footer2">
          <button
            className="category-modal-cancel2"
            type="button"
            onClick={onClose2}
            disabled={saving2}
          >
            Verwerfen
          </button>
          <button
            className="category-modal-save2"
            type="button"
            onClick={onSave2}
            disabled={saving2}
          >
            Speichern
          </button>
        </div>
      </div>
    </>
  );
};

// ===================
// SUBJECT MODAL
// ===================
const SubjectModal2 = ({
  open2,
  mode2, // "create" | "edit"
  facultyId2, // unused for now, but kept for later extensions
  subject2,
  initialCategoryId2, // used only for CREATE
  onClose2,
  onSaved2,
}) => {
  const isEdit2 = mode2 === "edit";

  // avoid eslint "unused-vars"
  void facultyId2;

  // initial fields
  const initialTitle2 = useMemo(
    () => (isEdit2 ? subject2?.title || "" : ""),
    [isEdit2, subject2]
  );
  const initialDesc2 = useMemo(
    () => (isEdit2 ? subject2?.description || "" : ""),
    [isEdit2, subject2]
  );
  const initialPublic2 = useMemo(
    () => (isEdit2 ? !!subject2?.isPublic : false),
    [isEdit2, subject2]
  );

  // ✅ NEW: initial imageUrl (only used for EDIT preview)
  const initialImageUrl2 = useMemo(
    () => (isEdit2 ? (subject2?.imageUrl || "").trim() : ""),
    [isEdit2, subject2]
  );

  const [title2, setTitle2] = useState(initialTitle2);
  const [desc2, setDesc2] = useState(initialDesc2);
  const [isPublic2, setIsPublic2] = useState(initialPublic2);

  // ✅ NEW: local image url preview state (EDIT only)
  const [imageUrl2, setImageUrl2] = useState(initialImageUrl2);

  const [touched2, setTouched2] = useState(false);
  const [error2, setError2] = useState("");

  const [createSubject2, { loading: creating2 }] = useMutation(
    CREATE_SUBJECT_IN_CATEGORY_MUTATION
  );
  const [updateSubject2, { loading: updatingSubject2 }] = useMutation(
    UPDATE_SUBJECT_MUTATION
  );

  useEffect(() => {
    if (!open2) return;
    setTitle2(initialTitle2);
    setDesc2(initialDesc2);
    setIsPublic2(initialPublic2);
    setImageUrl2(initialImageUrl2);
    setTouched2(false);
    setError2("");
  }, [open2, initialTitle2, initialDesc2, initialPublic2, initialImageUrl2]);

  const saving2 = creating2 || updatingSubject2;

  const validate2 = () => {
    if (!(title2 || "").trim()) return "Bitte Fachname eingeben.";
    if (!(desc2 || "").trim()) return "Bitte Kurz Fachbeschreibung eingeben.";

    if (!isEdit2 && !initialCategoryId2)
      return "Fehlende Fachkategorie (intern).";

    return "";
  };

  const onSave2 = async () => {
    setTouched2(true);
    const err = validate2();
    if (err) {
      setError2(err);
      return;
    }

    try {
      if (!isEdit2) {
        // CREATE: no subject yet => no need to fetch/show image from backend
        await createSubject2({
          variables: {
            input: {
              title: title2.trim(),
              description: desc2.trim(),
              facultyCategoryId: initialCategoryId2,
            },
          },
        });

        if (onSaved2) await onSaved2();
        return;
      }

      // EDIT: keep everything working; we are NOT changing imageUrl here
      await updateSubject2({
        variables: {
          input: {
            id: subject2.id,
            title: title2.trim(),
            description: desc2.trim(),
            isPublic: isPublic2,
            // imageUrl intentionally not changed here (you can add later when "Ändern" is implemented)
          },
        },
      });

      if (onSaved2) await onSaved2();
    } catch (e) {
      setError2("Speichern fehlgeschlagen. Bitte erneut versuchen.");
    }
  };

  const hasEditImage2 = isEdit2 && !!(imageUrl2 || "").trim();

  return (
    <>
      <div className="modal-backdrop2 modal-backdrop-edit2" onClick={onClose2} />

      <div className="subject-modal2" role="dialog" aria-modal="true">
        <div className="subject-modal-header2">
          <div className="subject-modal-title2">
            {isEdit2 ? "Fach bearbeiten" : "Fach erstellen"}
          </div>
          <button
            className="subject-modal-close2"
            type="button"
            aria-label="Schließen"
            onClick={onClose2}
          >
            ×
          </button>
        </div>

        <div className="subject-modal-content2">
          <div className="subject-modal-field2">
            <div className="subject-modal-label2">Fachname*</div>

            <div className="subject-modal-inputwrap2">
              <input
                className="subject-modal-input2"
                type="text"
                value={title2}
                onChange={(e) => {
                  setTitle2(e.target.value);
                  if (touched2) setError2(validate2());
                }}
                onBlur={() => {
                  setTouched2(true);
                  setError2(validate2());
                }}
                placeholder={isEdit2 ? "" : "Fachname eingeben"}
                disabled={saving2}
              />
              <div className="subject-modal-underline2" />
            </div>
          </div>

          <div className="subject-modal-field2">
            <div className="subject-modal-label2">Kurz Fachbeschreibung*</div>

            <div className="subject-modal-inputwrap2">
              <textarea
                className="subject-modal-input2 subject-modal-textarea2"
                value={desc2}
                onChange={(e) => {
                  setDesc2(e.target.value);
                  if (touched2) setError2(validate2());
                }}
                onBlur={() => {
                  setTouched2(true);
                  setError2(validate2());
                }}
                placeholder={
                  isEdit2 ? "" : "Kurz Fachbeschreibung eingeben"
                }
                disabled={saving2}
                rows={4}
              />
              <div className="subject-modal-underline2" />
            </div>
          </div>

          <div className="subject-modal-field2">
            <div className="subject-modal-label2">Cover Bild</div>
            <div className="subject-modal-imagebox2">
              {hasEditImage2 ? (
                <img
                  src={imageUrl2}
                  alt={title2 || "Subject"}
                  className="subject-modal-imagepreview2"
                  draggable="false"
                  onError={() => {
                    // if URL is broken -> fallback to placeholder
                    setImageUrl2("");
                  }}
                />
              ) : (
                <div className="subject-modal-imageiconwrap2" aria-hidden="true">
                  <svg
                    className="subject-modal-imageicon2"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v11A2.5 2.5 0 0 1 17.5 20h-11A2.5 2.5 0 0 1 4 17.5v-11Z"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <path
                      d="M8 14l2-2 3 3 2-2 3 3"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M15.5 9.5h.01"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              )}
            </div>

            <button
              type="button"
              className="subject-modal-changebtn2"
              disabled={saving2}
            >
              <SearchIcon2 className="subject-modal-searchicon2" />
              Cover ändern
            </button>
          </div>

          {/* publish toggle only for EDIT */}
          {isEdit2 && (
            <div className="subject-modal-publishrow2">
              <div className="subject-modal-publishmeta2">
                <div className="subject-modal-publishtitle2">
                  Veröffentlicht
                </div>
                <div className="subject-modal-publishtext2">
                  Sichtbar für Studierende und öffentlich zugänglich.
                </div>
              </div>

              <button
                type="button"
                className={`subject-modal-toggle2 ${isPublic2 ? "on2" : "off2"}`}
                onClick={() => setIsPublic2((v) => !v)}
                aria-label="Veröffentlichen umschalten"
              >
                <span className="subject-modal-toggleknob2" />
              </button>
            </div>
          )}

          {!!error2 && <div className="subject-modal-error2">{error2}</div>}
        </div>

        <div className="subject-modal-footer2">
          <button
            className="subject-modal-cancel2"
            type="button"
            onClick={onClose2}
            disabled={saving2}
          >
            Verwerfen
          </button>
          <button
            className="subject-modal-save2"
            type="button"
            onClick={onSave2}
            disabled={saving2}
          >
            Speichern
          </button>
        </div>
      </div>
    </>
  );
};

export default DashboardTutorEdit;
